import moment from 'moment';
import { Model } from './model';
import { type BlobModel, CaseModel, EntryModel, type FormFieldChanges, OTHER_CHOICE_CODE, type StudyChanges } from '@/models';
import { generateHexIdentifier, getOnlyTimeSeconds, getRandomAdjective, getUTCSeconds, getUTCSecondsAtStartOfDay, LocalStorageUtils } from '@/utils';
import type { DrillData, DrillLevel } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { getCurrentTimezone, getTimezone, type Timezone } from '@/components/forms/form_builder/reminder/TimezoneHelper';
import { getMatrixValues } from '@/components/data/entry/items/MatrixHelper';
import type { CustomIcon } from '@/components/common/interfaces/CustomIcon';
import { isAssignEffectPresent } from '@/components/data/entry/effects';
import type { GraphFilter } from './surgeonDashboard';
import { uuid } from 'vue-uuid';
import { DateTime } from 'luxon';

export interface NetworkForm {
  uri: string;
  hexIdentifier: string;
  identifier: string;
  creation: string;
  revisedOn: string;
  numFields: number;
  edit: boolean;
  title: string;
  investigationIdentifier?: string;

  enforcements?: FormEnforcements;
  items?: FormField[];
  anonymousResponsesAllowed?: boolean;
  reminders?: NetworkReminders;
  translations?: FormTranslation[];

  sharedForm?: boolean;

  entries?: EntryModel[];
  welcomeScreenTemplate?: string;
  archivedAt?: string;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;
}

export interface TimeSpentOnForm {
  averageEntryTime: number;
  minEntryTime: number;
  maxEntryTime: number;
}

interface NetworkReminders {
  type: string;
  recurrence: string;
  randomness: string | null;
  timezone: string | Timezone;
  ends: string | null;
  notification: ReminderNotification
}

interface FormStudy {
  identifier: string;
  scrambled: boolean;
}

export interface PostForm {
  title: string;
  studies: FormStudy[];
  description?: string;
  revisionDate: number;
  items: FormField[];
  anonymousResponsesAllowed: boolean;
  reminders?: Reminders;
}

export interface PostFormResponse {
  identifier: string;
}

export interface PostFormFile {
  filename: string;
  filetype: string;
  itemIdentifier?: string;
  size: number;
  withoutItemId: boolean;
}

export interface PresignedPostFields {
  key: string;
  bucket: string;
  'X-Amz-Algorithm': string;
  'X-Amz-Credential': string;
  'X-Amz-Date': string;
  Policy: string;
  'X-Amz-Signature': string;
}

export interface PresignedPost {
  url: string;
  fields: PresignedPostFields
}

export interface FormFileSignedResponse {
  presignedPost: PresignedPost;
  blob: NetworkBlob;
}

export interface FormEnforcements {
  enforceOrder: boolean;
  hidden: boolean;
  onlyOnce: false;
  order: number;
}

interface ReminderNotification {
  subject: string;
  body: string;
}

export interface Reminders {
  type: string;
  recurrence: string;
  randomness: string | null;
  timezone: Timezone;
  ends: string | null;
  notification: ReminderNotification
}

export interface FormTranslation {
  languageCode: string;
  language: string;
  title: string;
  description: string | null;
  items?: FormField[];
}

export interface IForm {
  identifier: string;
  createdAt: Date;
  updatedAt: Date;
  title: string;
  base64Id?: string;
  investigationIdentifier?: string;
  fieldCount: number;
  enforcements: FormEnforcements;
  anonymousResponsesAllowed?: boolean;
  reminders?: Reminders;
  formTranslations?: FormTranslation[];

  sharedForm?: boolean;
  welcomeScreenTemplate?: string;
  archivedAt?: string;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;
}

export class FormModel extends Model {
  identifier: string;
  createdAt: Date;
  updatedAt: Date;
  archivedAt?: Date;
  title: string;
  base64Id?: string;
  fieldCount: number;
  enforcements: FormEnforcements;
  studyId?: string;

  order?: number;
  items?: FormField[];
  anonymousResponsesAllowed: boolean;
  reminders?: Reminders;
  formTranslations?: FormTranslation[];

  sharedForm?: boolean;

  entries?: EntryModel[];
  welcomeScreenTemplate?: string;
  selectedCase?: CaseModel;
  entrySaveButtonText?: string;
  showSaveEntryButtonAtBottom?: boolean;
  hasEntryPermission?: boolean;
  timeSpentData?: TimeSpentOnForm;

  constructor({
    identifier,
    createdAt,
    updatedAt,
    title,
    base64Id,
    fieldCount,
    enforcements,
    anonymousResponsesAllowed,
    reminders,
    formTranslations,
    sharedForm,
    welcomeScreenTemplate,
    entrySaveButtonText,
    showSaveEntryButtonAtBottom,
    hasEntryPermission,
    investigationIdentifier,
    archivedAt,
    timeSpentData
  }: IForm) {
    super();
    this.identifier = identifier;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.title = title;
    this.base64Id = base64Id;
    this.fieldCount = fieldCount;
    this.enforcements = enforcements;
    this.anonymousResponsesAllowed = anonymousResponsesAllowed == true;
    this.reminders = reminders;
    this.formTranslations = formTranslations;
    this.sharedForm = sharedForm;
    this.welcomeScreenTemplate = welcomeScreenTemplate;
    this.entrySaveButtonText = entrySaveButtonText;
    this.showSaveEntryButtonAtBottom = showSaveEntryButtonAtBottom;
    this.hasEntryPermission = hasEntryPermission;
    this.studyId = investigationIdentifier;
    this.archivedAt = archivedAt ? moment(archivedAt).toDate() : undefined;
    this.timeSpentData = timeSpentData;
  }

  static mapFromBackend(form: NetworkForm) {
    if (form.reminders) {
      if (typeof form.reminders.timezone === 'string') {
        form.reminders!.timezone = getTimezone(form.reminders?.timezone) as Timezone;
      } else {
        form.reminders!.timezone = getTimezone(form.reminders?.timezone.value);
      }
    }
    
    const formModel = new FormModel({
      identifier: form.hexIdentifier ? form.hexIdentifier : form.identifier,
      createdAt: moment(form.creation).toDate(),
      updatedAt: moment(form.revisedOn).toDate(),
      title: form.title,
      base64Id: form.identifier,
      fieldCount: form.numFields,
      enforcements: form.enforcements ? form.enforcements : {
        enforceOrder: false,
        hidden: false,
        onlyOnce: false,
        order: 0
      },
      anonymousResponsesAllowed: form.anonymousResponsesAllowed,
      reminders: form.reminders as Reminders,
      formTranslations: form.translations,
      sharedForm: form.sharedForm,
      welcomeScreenTemplate: form.welcomeScreenTemplate,
      entrySaveButtonText: form.entrySaveButtonText,
      investigationIdentifier: form.investigationIdentifier,
      showSaveEntryButtonAtBottom: form.showSaveEntryButtonAtBottom,
      hasEntryPermission: form.hasEntryPermission,
      timeSpentData: form.timeSpentData
    });
    const items: FormField[] = [];
    if (form.items) {
      for (const item of form.items) {
        if (item.type === FormFieldType.CHOICE || item.type === FormFieldType.DROPDOWN) {
          if (typeof item.options.maximumChoicesCount === 'string') {
            item.options.maximumChoicesCount = parseInt(item.options.maximumChoicesCount);
          }
          item.options.maximumChoicesCount = item.options.maximumChoicesCount ? item.options.maximumChoicesCount : 2;
          item.options.allowOther = item.options.allowOther === true;

          if (item.options.choices) {
            let id = 1;
            item.options.choices.forEach(choice => {
              choice.id = id;
              id++;
            });
          }

          if (item.options.allowOther) {
            item.otherChoiceValue = {
              id: 99999999,
              label: 'Other',
              value: '-1',
              otherValue: ''
            };
          }
        }

        if (item.type === FormFieldType.FILE) {
          item.options.maximumSize = {
            enabled: item.options.maximumSize != null,
            value: item.options.maximumSize != null ? (item.options.maximumSize as number) : null
          };
          item.options.maximumFileNumber = {
            enabled: item.options.maximumFileNumber != null,
            value: item.options.maximumFileNumber != null ? (item.options.maximumFileNumber as number) : null
          };
          item.options.allowedExtensions = {
            enabled: item.options.allowedExtensions != null,
            value: item.options.allowedExtensions != null ? (item.options.allowedExtensions as string) : ''
          };
        }

        if (item.options.requirement) {
          item.nonStrictRequired = item.options.requirement === FIELD_REQUIREMENTS.NON_STRICT_REQUIRED;
          item.optional = item.options.requirement === FIELD_REQUIREMENTS.OPTIONAL;
          item.recommended = item.options.requirement === FIELD_REQUIREMENTS.RECOMMENDED;
        }

        if (item.type === FormFieldType.SECTION) {
          item.sectionExpanded = !item.options.collapsedInitially;

          item.options.fields?.forEach(field => {
            const foundField = form.items!.find(formItem => formItem.identifier == field);
            if (foundField != null) {
              foundField.sectionId = item.identifier;
            }
          });

          const sectionFields = item.options.fields;
          sectionFields?.forEach(fieldItem => {
            const foundField = form.items!.find(field => field.identifier === fieldItem);
            if (foundField) {
              foundField.expanded = item.sectionExpanded;
            }
          });
        }

        if (item.type === FormFieldType.UNIT && item.options.choices && item.options.choices.length > 0) {
          item.options.choices.push({
            id: 10,
            label: 'Clear',
            value: 'clear'
          });
        }

        if (isAssignEffectPresent(item)) {
          item.disable = true;
        }

        if (item.expanded != false) {
          item.expanded = true;
        }
        items.push(item);
      }
    }
    items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    formModel.items = items;
    formModel.order = form.enforcements?.order;
    return formModel;
  }

  static mapFormWithEntries(form: NetworkForm) {
    const formModel = new FormModel({
      identifier: form.hexIdentifier ? form.hexIdentifier : form.identifier,
      createdAt: moment(form.creation).toDate(),
      updatedAt: moment(form.revisedOn).toDate(),
      title: form.title,
      base64Id: form.identifier,
      fieldCount: form.numFields,
      enforcements: form.enforcements ? form.enforcements : {
        enforceOrder: false,
        hidden: false,
        onlyOnce: false,
        order: 0
      }
    });

    form.entries?.forEach(entry => {
      entry.createdAt = moment(entry.createdAt).toDate();
    });

    formModel.entries = form.entries;

    return formModel;
  }

  mapItemValues(updatedItems: Record<string, string | number>) {
    this.items?.forEach(field => {
      let value : string | number = field.value ? field.value : '';

      switch (field.type) {
        case FormFieldType.TEXT:
          value = field.value ? field.value : '';
          break;
        
        case FormFieldType.NUMBER:
          if (field.options.range) {
            value = field.value ? field.value : '';
          } else {
            value = field.numberValue !== undefined && field.numberValue !== null ? field.numberValue : '';
          }
          break;

        case FormFieldType.CHOICE:
          if (field.options.maximumChoicesCount == 1) {
            value = (field.choiceValue as FormFieldChoice)?.value;
            if (!value && field.options.allowOther && field.otherChoiceValue?.value === OTHER_CHOICE_CODE) {
              value = field.otherChoiceValue?.otherValue ?? '';
            }
          } else {
            const selectedChoices: string[] = [];
            field.options.choices?.forEach(choice => {
              if (choice.checked) {
                selectedChoices.push(`${choice.value}`);
              }
            });

            if (field.options.allowOther && field.otherChoiceValue?.checked) {
              selectedChoices.push(field.otherChoiceValue?.otherValue ?? '');
            }

            value = selectedChoices.join(',');
          }
          break;

        case FormFieldType.DROPDOWN:
          if (field.options.maximumChoicesCount == 1) {
            value = (field.choiceValue as FormFieldChoice)?.value;
          } else {
            const selectedChoices: string[] = [];
            (field.choiceValue as FormFieldChoice[])?.forEach(choice => {
              selectedChoices.push(`${choice.value}`);
            });

            value = selectedChoices.join(',');
          }
          break;
        
        case FormFieldType.DATE:
          if (field.dateValue) {
            if (field.options.mode === DateMode.DATE) {
              value = getUTCSecondsAtStartOfDay(field.dateValue);
            } else if (field.options.mode === DateMode.DATE_TIME) {
              if (field.options.absoluteDate) {
                value = getUTCSeconds(field.dateValue);
              } else {
                value = DateTime.fromJSDate(field.dateValue).toSeconds();
              }
              // Save the timezone info regardless of absolute date or not
              updatedItems[`${field.identifier}_data`] = JSON.stringify(field.timezoneRecorded);
            } else {
              value = getOnlyTimeSeconds(field.dateValue);
            }
          }
          break;

        case FormFieldType.LOCATION:

          if (field.locationValue) {
            // eslint-disable-next-line no-case-declarations
            const locationValue : NetworkLocationData = {
              latitude: field.locationValue.coords.latitude,
              longitude: field.locationValue.coords.longitude,
              accuracy: field.locationValue.coords.accuracy,
              timestamp: field.locationValue.timestamp
            };

            value = JSON.stringify(locationValue);
          }
          break;

        case FormFieldType.SLIDER:
          value = field.numberValue !== undefined && field.numberValue !== null ? field.numberValue : '';
          break;

        case FormFieldType.SIGNATURE:
          value = field.signatureValue ? JSON.stringify(field.signatureValue) : '';
          break;

        case FormFieldType.MATRIX:
          if (field.matrixTableRowValues) {
            // eslint-disable-next-line no-case-declarations
            const matrixValues = getMatrixValues(field.matrixTableRowValues);
            value = matrixValues ? JSON.stringify(matrixValues) : '';
          }
          break;

        case FormFieldType.FILE:
          if (field.fileIds) {
            value = JSON.stringify(field.fileIds);
          }
          break;

        case FormFieldType.DRILLDOWN:
          // eslint-disable-next-line no-case-declarations
          const selectedDrillLevels: string[] = [];
          if (field.drillValue && field.drillValue.length > 0) {
            (field.drillValue as DrillData[])!.forEach(level => {
              selectedDrillLevels.push(level.label);
            });
          }
          value = selectedDrillLevels.join(',');
          break;

        case FormFieldType.UNIT:
          if ((field.numberValue !== undefined && field.numberValue !== null) || field.choiceValue) {
            value = JSON.stringify({
              value: field.numberValue !== null ? field.numberValue : undefined,
              unit: field.value,
              inequality: (field.choiceValue as FormFieldChoice)?.value
            });
          } else {
            value = '';
          }
          break;
        default:
          break;
      }

      updatedItems[field.identifier] = value;
    });
  }
}

export interface PatchForm {
  surveyIdentifier: string;
  enforcements: FormEnforcements
}

export interface PatchFormsOrder {
  investigationId: string;
  surveys: PatchForm[]
}

export interface FormField {
  id: string;
  identifier: string;
  type: FormFieldType;
  label: string;
  description: string;
  conditions?: object;
  options: FormFieldOptions;
  formulaString: string;
  default: string;
  variableName?: string;

  icon?: CustomIcon;

  internalId?: string;
  expanded?: boolean;

  value?: string;
  numberValue?: number | null;
  choiceValue?: FormFieldChoice[] | FormFieldChoice;
  otherChoiceValue?: FormFieldChoice;
  dateValue?: Date;
  timezoneRecorded?: { timezone: string, overrideCalculatedValue?: boolean };
  overrideCalculatedValue?: boolean;
  locationValue?: GeolocationPosition;
  signatureValue?: SignatureData;
  fileIds?: string[];
  images?: {label: string, value: string}[];
  files?: BlobModel[];

  // matrix field related
  matrixValues?: MatrixValue[];
  matrixTableRowValues?: MatrixTableRow[];
  hasExistingMatrixValue?: boolean;
  matrixDependencyResolved?: number;

  drillValue?: DrillData[] | string[];

  //effects
  show?: boolean;
  warn?: string;
  error?: string;
  valid?: string;
  disable?: boolean;
  strictRequired?: boolean;
  nonStrictRequired?: boolean;
  optional?: boolean;
  recommended?: boolean;

  showRequiredError?: boolean;
  inputChanged?: boolean;

  invalidInput?: boolean;

  //import related
  changes?: FormFieldChanges;
  errors?: StudyChanges;

  blink?: boolean;

  sectionExpanded?: boolean;
  sectionId?: string;
  lastSectionField?: boolean;

  timeSeriesData?: TimeSeriesData[];
  historicalData?: TimeSeriesData[];
  bicarbonateData?: TimeSeriesData[];
  cumulativeTimeSeriesData?: TimeSeriesData[];


  //graph related
  filters?: GraphFilter[];
  liverCount?: number;
  plotType?: string;
  order?: number;
}

interface TimeSeriesData {
  perfusionSelectedTimepoint?: string;
  perfusionActualTimepoint: number;
  value: number;
  ymin: number;
  ymax: number;
  ymin2?: number;
  ymax2?: number;
}

export class FormFieldChoice {
  id: number;
  label: string;
  value: string | number;
  tooltip?: string;

  // for the multiple choice questions
  checked?: boolean;
  otherValue?: string;
  archived?: boolean;

  constructor(choice: IFormFieldChoice) {
    this.id = choice.id;
    this.label = choice.label;
    this.value = choice.value;
    this.tooltip = choice.tooltip;
    this.checked = choice.checked;
    this.otherValue = choice.otherValue;
    this.archived = choice.archived;
  }

  static createEmpty() {
    return new FormFieldChoice({
      id: 0,
      label: '',
      value: ''
    });
  }
}

export interface IFormFieldChoice {
  id: number;
  label: string;
  value: string | number;
  tooltip?: string;

  // for the multiple choice questions
  checked?: boolean;
  otherValue?: string;
  archived?: boolean;
}

export interface FileConfig {
  enabled: boolean;
  value: string | number | null;
}

export interface MatrixValue {
  rowId: string;
  values: MatrixColumnValue[];
  dynamicRows?: string[]
}

export interface MatrixColumnValue {
  id: string;
  value: string | number | boolean;
  timezoneRecorded?: { timezone: string } | string;
  conditions?: object;

  dateShifting?: boolean;
}

export interface MatrixRow {
  id: number;
  label: string;
  dynamicRowConfig?: DynamicRowConfig
}

interface DynamicRowConfig {
  copyRowData: boolean;
  copyRowHeader: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  conditions?: any[];
  rowsAdded?: number;
}

export interface MatrixTableRow {
  rowId: string;
  rowHeader: string;
  dynamicRowConfig?: DynamicRowConfig;

  dynamicRows?: string[];
  enableClear?: boolean;
  columnValues?: MatrixColumn[]
}

interface DynamicRowColumnFormula {
  columnId: number;
  operator: string;
}

interface ColumnFormula {
  columnId: number;
  rowId: number;
  destColumnId: number;
  operator: string;
}

export interface MatrixColumn {
  id: number;
  label: string;
  type: MatrixColumnType;
  tintColor?: string;
  options?: MatrixColumnOptions;

  disable?: boolean;
  key?: string;
  dynamicRowColumnFormula?: DynamicRowColumnFormula
  columnFormula?: ColumnFormula

  textValue?: string;
  numberValue?: number | null;
  booleanValue?: boolean;
  dateValue?: Date;
  unShiftedDate?: Date;
  timezoneRecorded?: { timezone: string };
  choiceValue?: FormFieldChoice[] | FormFieldChoice;

  //table column related
  frozen?: boolean;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default?: any[];

  tooltip?: string;

  variableName?: string;

  conditions?: object;

  //file realted
  fileIds?: string[];

  warn?: string;
}

export interface MatrixFieldUploadedFile {
  identifier: string;
  rowId: string;
  columnId: string;
  files: BlobModel[];
}

interface UnitConfig {
  list: Unit[],
  default?: number,
  defaultFixed?: boolean
}

interface Unit {
  id: number;
  unit: string;
  description: string;
  conversions: object;
  fromFieldValue?: string;
}

export interface NetworkBlob {
  identifier: string;
  name: string;
  contentType: string;
  size: number;
  storedAt: string;
  hasThumbnail: boolean;
  thumbnailKey?: string;
}

export interface NumberRange {
  rangeStart: number;
  rangeEnd: number;
}

interface FormFieldOptions {
  choices?: FormFieldChoice[];
  maximumChoicesCount?: number;
  allowOther?: boolean;
  mode?: DateMode | MatrixMode;
  absoluteDate?: boolean;
  dateShifting?: boolean;
  dateConstraints?: { min?: number | string; max?: number | string; };
  allowedExtensions?: FileConfig | string;
  maximumSize?: FileConfig | number;
  maximumFileNumber?: FileConfig | number;
  incrementSize?: number;
  startValue?: number;
  endValue?: number;
  startLabel?: string;
  endLabel?: string;
  numberOfSignatories?: string;
  allowEdit?: boolean;

  rows?: MatrixRow[];
  columns?: MatrixColumn[];

  //drilldown
  file?: string;
  drillLevels?: DrillLevel[];
  blob?: BlobModel;

  //new fields related
  units?: UnitConfig;
  format?: string;
  timeFormat?: number;

  requirement?: FIELD_REQUIREMENTS;

  fields?: string[];
  collapsedInitially?: boolean;

  overrideCalculation?: boolean;

  // Label tooltip
  tooltip?: string;


  range?: NumberRange
}

// eslint-disable-next-line no-shadow
export enum FIELD_REQUIREMENTS {
  STRICT_REQUIRED='STRICT_REQUIRED',
  NON_STRICT_REQUIRED='NON_STRICT_REQUIRED',
  OPTIONAL='OPTIONAL',
  RECOMMENDED='RECOMMENDED'
}

// eslint-disable-next-line no-shadow
export enum FormFieldType {
  NUMBER = 'Number',
  TEXT = 'Text',
  CHOICE = 'Choice',
  DROPDOWN = 'Dropdown',
  DATE = 'Date',
  LOCATION = 'Location',
  FILE = 'File',
  TITLE = 'Title',
  SLIDER = 'Slider',
  BARCODE = 'Barcode',
  SIGNATURE = 'Signature',
  MATRIX = 'Matrix',
  DRILLDOWN = 'Drilldown',

  // New fields
  UNIT='Unit',
  SECTION='Section',

  //Client only types
  BRANDING='Branding',
  SUBMIT_BUTTON='SubmitButton'
}

// eslint-disable-next-line no-shadow
export enum DateMode {
  DATE = 'date',
  TIME = 'time',
  DATE_TIME = 'datetime'
}

// eslint-disable-next-line no-shadow
export enum MatrixMode {
  RANK = 'rank',
}

export const FIELD_TYPES = [
  {
    id: 0,
    type: FormFieldType.NUMBER,
    label: 'Number',
    iconStyle: 'far',
    icon: 'hashtag'
  },
  {
    id: 1,
    type: FormFieldType.TEXT,
    label: 'Text',
    iconStyle: 'far',
    icon: 'text'
  },
  {
    id: 2,
    type: FormFieldType.CHOICE,
    label: 'Choice',
    iconStyle: 'far',
    icon: 'list-ul'
  },
  {
    id: 3,
    type: FormFieldType.DROPDOWN,
    label: 'Dropdown',
    iconStyle: 'far',
    icon: 'square-caret-down'
  },
  {
    id: 4,
    type: FormFieldType.DATE,
    label: 'Date',
    iconStyle: 'far',
    icon: 'calendar-days'
  },
  {
    id: 5,
    type: FormFieldType.LOCATION,
    label: 'GPS Location',
    iconStyle: 'far',
    icon: 'location-crosshairs'
  },
  {
    id: 6,
    type: FormFieldType.FILE,
    label: 'File',
    iconStyle: 'far',
    icon: 'file-arrow-up'
  },
  {
    id: 7,
    type: FormFieldType.TITLE,
    label: 'Title',
    iconStyle: 'far',
    icon: 'quote-left'
  },
  {
    id: 8,
    type: FormFieldType.SLIDER,
    label: 'Slider',
    iconStyle: 'far',
    icon: 'square-sliders'
  },
  {
    id: 9,
    type: FormFieldType.BARCODE,
    label: 'Barcode',
    iconStyle: 'far',
    icon: 'barcode-read'
  },
  {
    id: 10,
    type: FormFieldType.SIGNATURE,
    label: 'Signature',
    iconStyle: 'far',
    icon: 'file-signature'
  },
  {
    id: 11,
    type: FormFieldType.MATRIX,
    label: 'Matrix',
    iconStyle: 'fas',
    icon: 'table'
  },
  {
    id: 12,
    type: FormFieldType.DRILLDOWN,
    label: 'Drilldown',
    iconStyle: 'far',
    icon: 'list-tree'
  },
  {
    id: 13,
    type: FormFieldType.UNIT,
    label: 'Unit',
    iconStyle: 'far',
    icon: 'weight-hanging'
  },
  {
    id: 14,
    type: FormFieldType.SECTION,
    label: 'Section',
    iconStyle: 'fal',
    icon: 'file-half-dashed'
  }
];

export const createSampleFormPayload = (studyIdentifier: string): PostForm => {
  const username = LocalStorageUtils.getUserKeys().name?.split(' ')[0] || 'User';
  const title = `${username}'s ${getRandomAdjective()} form`;

  const studies = [{
    identifier: studyIdentifier,
    scrambled: true
  }];

  const item = createFormField();
  item.expanded = undefined;

  return {
    studies,
    title,
    revisionDate: new Date().getTime() / 1000,
    items: [item],
    description: '',
    anonymousResponsesAllowed: false
  };
};

export const createFormField = (): FormField => {
  return {
    id: uuid.v4(),
    identifier: generateHexIdentifier(6),
    type: FormFieldType.NUMBER,
    label: 'Example number field',
    description: '',
    conditions: [],
    options: {},
    formulaString: '',
    default: '',
    expanded: true
  };
};

export const getEmptyReminderConfig = () => ({
  type: 'rolling',
  recurrence: '0 10 * * *',
  randomness: null,
  timezone: getCurrentTimezone(),
  ends: null,
  notification: {
    subject: 'Time to complete a form',
    body: 'Don\'t forgot to log your data'
  }
});

// eslint-disable-next-line no-shadow
export enum MatrixColumnType {
  TEXT = 'Text',
  NUMBER = 'Number',
  SINGLE_CHOICE = 'RadioButton',
  MULTIPLE_CHOICE = 'Checkbox',
  DATE = 'Date',
  DROPDOWN = 'Dropdown',
  FILE = 'File',
}

export const MATRIX_COLUMN_TYPES = [
  {
    id: 0,
    type: MatrixColumnType.TEXT,
    label: 'Text',
    iconStyle: 'far',
    icon: 'text'
  },
  {
    id: 1,
    type: MatrixColumnType.NUMBER,
    label: 'Number',
    iconStyle: 'far',
    icon: 'hashtag'
  },
  {
    id: 2,
    type: MatrixColumnType.SINGLE_CHOICE,
    label: 'Single Choice',
    iconStyle: 'far',
    icon: 'dot-circle'
  },
  {
    id: 3,
    type: MatrixColumnType.MULTIPLE_CHOICE,
    label: 'Multiple choice',
    iconStyle: 'far',
    icon: 'check-square'
  },
  {
    id: 4,
    type: MatrixColumnType.DATE,
    label: 'Date',
    iconStyle: 'far',
    icon: 'calendar-alt'
  },
  {
    id: 5,
    type: MatrixColumnType.DROPDOWN,
    label: 'Dropdown',
    iconStyle: 'far',
    icon: 'square-caret-down'
  }
];

interface MatrixColumnOptions {
  mode?: string;
  placeholder?: string;
  choices?: FormFieldChoice[];
  multipleChoice?: boolean;
  format?: string;
  timeFormat?: number;
  absoluteDate?: boolean;

  dateConstraints?: { min?: number | string; max?: number | string; };
  dateShifting?: boolean;

  showingShiftedDate?: boolean;

  range?: NumberRange;
}

// eslint-disable-next-line no-shadow
export enum FormFieldEffects {
  WARN='Warn',
  SHOW='Show',
  VALID='Valid',
  ASSIGN='Assign',
  DISABLE='Disable',
  REQUIRED='Required',
  DEFAULT='Default'
}

// Field type value interfaces

export interface GeolocationPosition {
  coords: Coordinates;
  timestamp: number;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy: number;
  altitudeAccuracy?: number | null;
  heading?: number | null;
  speed?: number | null; 
}

export interface PositionErrorCallback {
  (positionError: GeolocationPositionError): void;
}

interface GeolocationPositionError {
  code: number;
  message: string;
  PERMISSION_DENIED: number;
  POSITION_UNAVAILABLE: number;
  TIMEOUT: number;
}

export interface Signatory {
  signature: string;
  signatory: string;
  email: string;
  dateTime: number;
  emailSent?: boolean;
  revoked?: boolean;
  removed?: boolean;
}

export interface SignatureData {
  signatories: Signatory[];
}

export interface NetworkLocationData {
  latitude: number;
  longitude: number;
  accuracy: number,
  timestamp: number;
}

// eslint-disable-next-line no-shadow
export enum DELETE_CONFIRMATION_TYPE {
  FORM='form',
  ARCHIVE='Archive'
}
